import { Injectable } from '@angular/core';
import {
  ProductionHistory,
  Permissions,
  Stations,
  LoginModel
} from '../Models/ApplicationModels';
import { BehaviorSubject, of } from 'rxjs';
import { MasterDataSeriveService } from './master-data-serive.service';
import { DatePipe } from '@angular/common';
import { map, catchError, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  constructor(
    private masterservice: MasterDataSeriveService,
    private datePipe: DatePipe
  ) {}

  private _activeDept: BehaviorSubject<string> = new BehaviorSubject<string>(
    ''
  );
  private _activeCategory: BehaviorSubject<string> =
    new BehaviorSubject<string>('');
  private _activeSubCategory: BehaviorSubject<string> =
    new BehaviorSubject<string>('');
  private _perDayEntries: BehaviorSubject<number> = new BehaviorSubject<number>(
    0
  );
  private _activeStationUser: BehaviorSubject<Stations> =
    new BehaviorSubject<Stations>(new Stations());
  private counterSubject = new BehaviorSubject<number>(0);
  public counter$ = this.counterSubject.asObservable();

  userData: any;
  currentDate: Date = new Date();
  allEntries: Array<ProductionHistory> = [];
  matchQuantities: any[] = [];
  totalEnteries: number = 0;
  userInfo: LoginModel = new LoginModel();

  // The local running total is only ever a display optimization between
  // resyncs. It used to be cached under a single device-level key (not
  // user-scoped) and never reconciled with the server intraday, so a missed
  // update (network error, page reload mid-request) silently and permanently
  // under-counted for the rest of the day, and a station shared by multiple
  // employees carried one user's running total into the next user's session.
  // Scoping the cache key by userId fixes the cross-user leak; bounding drift
  // with a resync every few actions/minutes fixes the permanent-desync case
  // without paying for a server round trip on every single item.
  private static readonly RESYNC_ACTION_THRESHOLD = 5;
  private static readonly RESYNC_INTERVAL_MS = 2 * 60 * 1000;
  private actionsSinceSync = 0;
  private resyncTimerStarted = false;

  private getCurrentUserId(): number | null {
    const userData = this.masterservice.getUserDetailsFromStorage();
    return userData?.userId ?? null;
  }

  private itemsKey(userId: number): string {
    return `Items_${userId}`;
  }

  private itemsLastUpdatedKey(userId: number): string {
    return `ItemsLastUpdated_${userId}`;
  }

  private isSameDayAsNow(timestamp: number): boolean {
    const a = new Date(timestamp);
    const b = new Date();
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private ensureResyncTimer(): void {
    if (this.resyncTimerStarted) {
      return;
    }
    this.resyncTimerStarted = true;
    setInterval(() => void this.getProductionCount(), BreadcrumbService.RESYNC_INTERVAL_MS);
  }

  async updatePerDaytEnteries(forceRefresh: boolean = false): Promise<void> {
    const userId = this.getCurrentUserId();
    if (userId === null) {
      return;
    }

    const items = localStorage.getItem(this.itemsKey(userId));
    const lastUpdated = localStorage.getItem(this.itemsLastUpdatedKey(userId));
    const isStale = !lastUpdated || !this.isSameDayAsNow(Number(lastUpdated));

    if (items === null || forceRefresh || isStale) {
      await this.getProductionCount();
    } else {
      this._perDayEntries.next(parseInt(items, 10));
    }

    this.ensureResyncTimer();
  }

  async getProductionCount() {
    const userId = this.getCurrentUserId();
    if (userId === null) {
      return;
    }

    this.masterservice.getProductionQuantity(userId).subscribe({
      next: (quantity: any) => {
        localStorage.setItem(this.itemsKey(userId), quantity.toString());
        localStorage.setItem(this.itemsLastUpdatedKey(userId), Date.now().toString());
        this._perDayEntries.next(quantity);
        this.actionsSinceSync = 0;
      },
      error: (error) => {
        console.error('Error fetching production count:', error);
      }
    });
  }

  getCounterValue(): number {
    return this.counterSubject.value;
  }

  private trackActionForResync(): void {
    this.actionsSinceSync++;
    this.ensureResyncTimer();
    if (this.actionsSinceSync >= BreadcrumbService.RESYNC_ACTION_THRESHOLD) {
      void this.getProductionCount();
    }
  }

  increment(quantity: any): void {
    const userId = this.getCurrentUserId();
    if (userId === null) {
      return;
    }

    const storedValue = localStorage.getItem(this.itemsKey(userId)) || '0';
    const currentValue: number = parseInt(storedValue, 10) + parseInt(quantity, 10);
    this._perDayEntries.next(currentValue);
    localStorage.setItem(this.itemsKey(userId), currentValue.toString());
    localStorage.setItem(this.itemsLastUpdatedKey(userId), Date.now().toString());
    this.trackActionForResync();
  }

  decrement(quantity: any): void {
    const userId = this.getCurrentUserId();
    if (userId === null) {
      return;
    }

    const storedValue = localStorage.getItem(this.itemsKey(userId)) || '0';
    const currentValue: number = parseInt(storedValue, 10) - parseInt(quantity, 10);
    if (currentValue >= 0) {
      this._perDayEntries.next(currentValue);
      localStorage.setItem(this.itemsKey(userId), currentValue.toString());
      localStorage.setItem(this.itemsLastUpdatedKey(userId), Date.now().toString());
    }
    this.trackActionForResync();
  }

  updateActiveDeptTab(newName: string) {
    this._activeDept.next(newName);
  }

  updateActiveCategoryTab(newName: string) {
    this._activeCategory.next(newName);
  }
  updateActiveSubCategoryTab(newName: string) {
    this._activeSubCategory.next(newName);
  }

  get activeDeptTab$() {
    return this._activeDept.asObservable();
  }

  get activeCategoryTab$() {
    return this._activeCategory.asObservable();
  }
  get activeSubCategoryTab$() {
    return this._activeSubCategory.asObservable();
  }

  get totalItemEnterisPerDay$() {
    return this._perDayEntries.asObservable();
  }

  get activeStationUser$() {
    return this._activeStationUser.asObservable();
  }
}
